"use client";

import { useRef, useState } from "react";
import {
  CopyButton,
  Notice,
  type Option,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  AuthorCard,
  type AuthorRow,
} from "@/components/tools/publishing-statements/author-card";
import {
  ExampleButton,
  NoticeList,
  RichParagraphs,
  SmallButton,
} from "@/components/tools/publishing-statements/kit";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  type AuthorInput,
  blockSections,
  blockToHtml,
  blockToLatex,
  blockToPlain,
  buildAuthorBlock,
  type EqualSymbolId,
  type FinalJoin,
  type MarkerStyle,
  parseOrcid,
} from "@/lib/tool-kit/publishing/affiliations";

/** English strings. A translated content file overrides these through `ui`. */
export const AUTHOR_AFFILIATION_FORMATTER_STRINGS = {
  authorPanel: "Author {number}",
  nameLabel: "Name, as it should appear",
  namePlaceholder: "e.g. Maya Okafor",
  orcidLabel: "ORCID iD (optional)",
  orcidPlaceholder: "0000-0000-0000-0000",
  orcidValid: "Valid ORCID iD.",
  orcidChecksum:
    "This iD fails ORCID's check digit — look for a mistyped digit.",
  orcidFormat:
    "An ORCID iD has 16 characters in four groups of four, the last of which may be X.",
  affiliationLabel: "Affiliation {number}",
  affiliationPlaceholder:
    "e.g. Department of Psychology, University of Eastbrook, Eastbrook, UK",
  addAffiliation: "Add affiliation",
  removeAffiliation: "Remove",
  removeAffiliationAria: "Remove affiliation {number} of author {author}",
  correspondingCheckbox: "Corresponding author",
  equalLabel: "Contributed equally",
  emailLabel: "Email for correspondence",
  emailPlaceholder: "e.g. name@university.edu",
  moveUp: "Move up",
  moveDown: "Move down",
  moveUpAria: "Move author {number} up",
  moveDownAria: "Move author {number} down",
  removeAuthor: "Remove author",
  removeAuthorAria: "Remove author {number}",
  addAuthor: "Add author",
  markersLabel: "Affiliation marks",
  markersNumbers: "Numbers (1, 2, 3)",
  markersLetters: "Letters (a, b, c)",
  equalSymbolLabel: "Equal-contribution mark",
  symbolDagger: "† dagger",
  symbolDoubleDagger: "‡ double dagger",
  symbolHash: "# hash",
  symbolYinyang: "☯ (PLOS)",
  joinLabel: "Before the last name",
  joinComma: "A comma",
  joinAnd: "“and”",
  joinAmpersand: "“&”",
  viewLabel: "Show as",
  viewRich: "Formatted",
  viewPlain: "Plain text",
  viewHtml: "HTML",
  viewLatex: "LaTeX (authblk)",
  tryExample: "Try an example",
  exampleNames: ["Maya Okafor", "Daniel Ruiz", "Priya Raman"],
  exampleAffiliations: [
    "Department of Psychology, University of Eastbrook, Eastbrook, UK",
    "Centre for Sleep Research, Halden Institute, Oslo, Norway",
    "School of Public Health, Northgate University, Northgate, UK",
  ],
  exampleOrcid: "0000-0002-1825-0097",
  exampleEmail: "m.okafor@eastbrook.example",
  results: "Your author block",
  copy: "Copy",
  copied: "Copied!",
  prompt: "Enter at least one author's name to build the author block.",
  noticeEqualOne:
    "Only one author is marked as contributing equally. Mark everyone who shares equal credit.",
  noticeNoCorresponding: "Mark at least one corresponding author.",
  noticeNoAffiliation:
    "one: {names} has no affiliation yet.|other: {names} have no affiliation yet.",
  noticeEmail: "Add an email address for each corresponding author.",
  noticeOrcid:
    "ORCID iDs that fail the check are left out of the output until they are fixed.",
  listSeparator: ", ",
  nameSeparator: ", ",
  finalAnd: " and ",
  finalAmpersand: " & ",
  equalNote: "These authors contributed equally to this work.",
  correspondingNote: "{label}: {emails}",
  correspondingNoteLabel:
    "one: Corresponding author|other: Corresponding authors",
  placeholderEmail: "[email for {name}]",
  orcidHeading: "ORCID iDs",
  orcidLine: "{name}: {url}",
  emailSeparator: "; ",
  latexPreamble: "% Preamble",
  latexBody: "% Title page, before \\maketitle",
} as const;

type Strings = typeof AUTHOR_AFFILIATION_FORMATTER_STRINGS;
type View = "rich" | "plain" | "html" | "latex";

const MAX_AUTHORS = 60;

function emptyAuthor(id: number, affiliationId: number): AuthorRow {
  return {
    id,
    name: "",
    affiliations: [{ id: affiliationId, text: "" }],
    orcid: "",
    email: "",
    corresponding: false,
    equal: false,
  };
}

function toInput(row: AuthorRow): AuthorInput {
  return { ...row, affiliations: row.affiliations.map((item) => item.text) };
}

function noticesFor(
  rows: readonly AuthorRow[],
  output: string,
  strings: Strings,
  locale: string
): string[] {
  const named = rows.filter((row) => row.name.trim() !== "");
  const notes: string[] = [];
  if (named.filter((row) => row.equal).length === 1) {
    notes.push(strings.noticeEqualOne);
  }
  if (named.length > 0 && !named.some((row) => row.corresponding)) {
    notes.push(strings.noticeNoCorresponding);
  }
  const bare = named.filter((row) =>
    row.affiliations.every((item) => item.text.trim() === "")
  );
  if (bare.length > 0) {
    notes.push(
      tPlural(strings.noticeNoAffiliation, locale, bare.length, {
        names: bare.map((row) => row.name.trim()).join(strings.listSeparator),
      }).trim()
    );
  }
  if (named.some((row) => row.corresponding && row.email.trim() === "")) {
    notes.push(strings.noticeEmail);
  }
  if (
    named.some((row) => {
      const status = parseOrcid(row.orcid).status;
      return status === "checksum" || status === "format";
    })
  ) {
    notes.push(strings.noticeOrcid);
  }
  return output === "" ? [] : notes;
}

function CodeView({ text, strings }: { text: string; strings: Strings }) {
  return (
    <div className={`${RESULT_CARD} space-y-3`}>
      <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-muted/50 p-3 font-mono text-foreground text-xs leading-relaxed">
        {text}
      </pre>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={text}
      />
    </div>
  );
}

export default function AuthorAffiliationFormatterWidget() {
  const strings = useToolUi(AUTHOR_AFFILIATION_FORMATTER_STRINGS);
  const locale = useToolLocale();
  const counter = useRef(100);
  const [rows, setRows] = useState<AuthorRow[]>([
    emptyAuthor(1, 2),
    emptyAuthor(3, 4),
  ]);
  const [markers, setMarkers] = useState<MarkerStyle>("numbers");
  const [equalSymbol, setEqualSymbol] = useState<EqualSymbolId>("dagger");
  const [join, setJoin] = useState<FinalJoin>("comma");
  const [view, setView] = useState<View>("rich");

  const takeId = () => {
    counter.current += 1;
    return counter.current;
  };

  const block = buildAuthorBlock(rows.map(toInput), { markers, equalSymbol });
  const correspondingCount = block.authors.filter(
    (author) => author.corresponding
  ).length;
  const templates = {
    ...strings,
    correspondingLabel: tPlural(
      strings.correspondingNoteLabel,
      locale,
      correspondingCount
    ).trim(),
  };
  const sections = blockSections(
    block,
    { markers, equalSymbol, join },
    templates
  );
  const plain = blockToPlain(sections);
  const notes = noticesFor(rows, plain, strings, locale);
  const output = {
    plain,
    html: blockToHtml(sections),
    latex: blockToLatex(
      block,
      { markers, equalSymbol },
      {
        ...templates,
        preambleComment: strings.latexPreamble,
        bodyComment: strings.latexBody,
      }
    ),
  };

  function move(index: number, offset: number) {
    setRows((current) => {
      const next = [...current];
      const target = index + offset;
      if (target < 0 || target >= next.length) {
        return current;
      }
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function loadExample() {
    const [first, second, third] = strings.exampleAffiliations;
    const make = (
      name: string,
      affiliations: string[],
      extra: Partial<AuthorRow> = {}
    ): AuthorRow => ({
      ...emptyAuthor(takeId(), 0),
      name,
      affiliations: affiliations.map((text) => ({ id: takeId(), text })),
      ...extra,
    });
    const [maya, daniel, priya] = strings.exampleNames;
    setRows([
      make(maya, [first, second], {
        orcid: strings.exampleOrcid,
        corresponding: true,
        email: strings.exampleEmail,
        equal: true,
      }),
      make(daniel, [second], { equal: true }),
      make(priya, [first, third]),
    ]);
  }

  const symbolOptions: Option<EqualSymbolId>[] = [
    { value: "dagger", label: strings.symbolDagger },
    { value: "doubleDagger", label: strings.symbolDoubleDagger },
    { value: "hash", label: strings.symbolHash },
    { value: "yinyang", label: strings.symbolYinyang },
  ];
  const joinOptions: Option<FinalJoin>[] = [
    { value: "comma", label: strings.joinComma },
    { value: "and", label: strings.joinAnd },
    { value: "ampersand", label: strings.joinAmpersand },
  ];
  const viewOptions: Option<View>[] = [
    { value: "rich", label: strings.viewRich },
    { value: "plain", label: strings.viewPlain },
    { value: "html", label: strings.viewHtml },
    { value: "latex", label: strings.viewLatex },
  ];
  const groups = [
    [sections.authorLine],
    sections.affiliations,
    sections.notes,
    sections.orcids,
  ].filter((group) => group.length > 0 && group[0].length > 0);

  return (
    <div className="space-y-6">
      {rows.map((row, index) => (
        <AuthorCard
          count={rows.length}
          index={index}
          key={row.id}
          nextId={takeId}
          onChange={(patch) =>
            setRows((current) =>
              current.map((item) =>
                item.id === row.id ? { ...item, ...patch } : item
              )
            )
          }
          onMove={(offset) => move(index, offset)}
          onRemove={() =>
            setRows((current) => current.filter((item) => item.id !== row.id))
          }
          row={row}
          strings={strings}
        />
      ))}
      <SmallButton
        disabled={rows.length >= MAX_AUTHORS}
        label={strings.addAuthor}
        onClick={() =>
          setRows((current) => [...current, emptyAuthor(takeId(), takeId())])
        }
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <PillGroup<MarkerStyle>
          label={strings.markersLabel}
          name="aaf-markers"
          onChange={setMarkers}
          options={[
            { value: "numbers", label: strings.markersNumbers },
            { value: "letters", label: strings.markersLetters },
          ]}
          value={markers}
        />
        <SelectField<EqualSymbolId>
          id="aaf-symbol"
          label={strings.equalSymbolLabel}
          onChange={setEqualSymbol}
          options={symbolOptions}
          value={equalSymbol}
        />
        <SelectField<FinalJoin>
          id="aaf-join"
          label={strings.joinLabel}
          onChange={setJoin}
          options={joinOptions}
          value={join}
        />
      </div>
      <ExampleButton label={strings.tryExample} onClick={loadExample} />

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {plain === "" ? (
          <Notice>{strings.prompt}</Notice>
        ) : (
          <>
            <PillGroup<View>
              label={strings.viewLabel}
              name="aaf-view"
              onChange={setView}
              options={viewOptions}
              value={view}
            />
            {view === "rich" ? (
              <div className={`${RESULT_CARD} space-y-4`}>
                {groups.map((group, index) => (
                  // Sections are positional and never reorder.
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional sections
                  <RichParagraphs key={index} paragraphs={group} />
                ))}
                <CopyButton
                  copiedLabel={strings.copied}
                  html={output.html}
                  label={strings.copy}
                  small
                  text={output.plain}
                />
              </div>
            ) : (
              <CodeView strings={strings} text={output[view]} />
            )}
          </>
        )}
        <NoticeList items={notes} />
      </section>
    </div>
  );
}
